import { AddDeploymentRingModal } from "modules/field-mgmt/components/AddDeploymentRingModal";
import React, { useState } from "react";

import {
  confirm,
  DefaultButton,
  ScrollablePane,
  ScrollbarVisibility,
  Stack
} from "@bps/fluent-ui";
import { QueryStateIndicator } from "@components/QueryStateIndicator";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import {
  DeploymentRingFieldDevice,
  DeploymentRingFieldDeviceDetail,
  FieldDevice
} from "@libs/api/gateways/field/field-ops-gateway.dtos";
import {
  FieldOpsCacheKeys,
  useAddDeviceDeploymentRing,
  useDeleteDeviceDeploymentRing,
  useFieldDeviceDeploymentRings
} from "@libs/api/gateways/field/field-ops-gateway.hooks";
import { NO_ACTION_PERMISSION } from "@libs/permissions/permissions.constants";
import { Permissions } from "@libs/permissions/permissions.enum";

import { FieldDeviceDeploymentRingTable } from "./FieldDeviceDeploymentRingTable";

interface FieldDeviceDeploymentRingListProps {
  device: FieldDevice;
}

export const FieldDeviceDeploymentRingList: React.FC<FieldDeviceDeploymentRingListProps> = ({
  device
}) => {
  const [showAddFieldDevice, setShowAddFieldDevice] = useState<boolean>(false);

  const {
    mutateAsync: deleteDeviceDeploymentRing
  } = useDeleteDeviceDeploymentRing(device.id);

  const { mutateAsync: addDeviceDeploymentRing } = useAddDeviceDeploymentRing();

  const onRemoveItem = async (
    fieldDeviceDeploymentRingDetail: DeploymentRingFieldDeviceDetail
  ) => {
    const isConfirmed = await confirm({
      confirmButtonProps: {
        text: "Remove"
      },
      cancelButtonProps: {
        text: "Cancel"
      },
      dialogContentProps: {
        title: `Remove deployment ring ${fieldDeviceDeploymentRingDetail.code}?`,
        subText:
          "Are you sure you want to remove this deployment ring from this device?"
      }
    });
    if (isConfirmed) {
      await deleteDeviceDeploymentRing(fieldDeviceDeploymentRingDetail.id);
    }
  };

  const sortBySortOrder = (
    fieldDeviceDeploymentRingDetails: DeploymentRingFieldDevice[]
  ) =>
    fieldDeviceDeploymentRingDetails.sort((a, b) => a.sortOrder - b.sortOrder);

  const query = useFieldDeviceDeploymentRings(device.id);

  const { data: hasWritePermission } = usePermissionsCheckQuery(
    Permissions.PltFieldManagementWrite
  );

  return (
    <>
      <Stack>
        <Stack.Item align="end">
          <DefaultButton
            disabled={!hasWritePermission}
            title={!hasWritePermission ? NO_ACTION_PERMISSION : undefined}
            iconProps={{ iconName: "Add" }}
            onClick={() => {
              setShowAddFieldDevice(!showAddFieldDevice);
            }}
          >
            Add Deployment Ring
          </DefaultButton>
        </Stack.Item>
      </Stack>

      <QueryStateIndicator<DeploymentRingFieldDevice[]>
        allowNullOrUndefined
        {...query}
      >
        {fieldDeviceDeploymentRings => (
          <>
            <AddDeploymentRingModal
              show={showAddFieldDevice}
              title="Add Deployment Ring"
              existingDeploymentRingIds={
                fieldDeviceDeploymentRings?.map(r => r.deploymentRingId) ?? []
              }
              onDismiss={() => setShowAddFieldDevice(false)}
              addDeploymentRing={async ring =>
                await addDeviceDeploymentRing({
                  fieldDeviceId: device.id,
                  deploymentRingId: ring.id,
                  sortOrder: 0
                })
              }
              refreshCacheKey={FieldOpsCacheKeys.DeploymentRingFieldDevice}
            />
            <Stack
              grow
              styles={{ root: { position: "relative" } }}
              verticalFill
            >
              <ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto}>
                <FieldDeviceDeploymentRingTable
                  items={sortBySortOrder(fieldDeviceDeploymentRings)}
                  onRemoveItem={onRemoveItem}
                  device={device}
                />
              </ScrollablePane>
            </Stack>
          </>
        )}
      </QueryStateIndicator>
    </>
  );
};
